import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Grid,
  List,
  Stack,
  TextField,
  Typography,
  Button,
  Alert,
  Snackbar,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Switch
} from "@mui/material";
import _ from "lodash";
import Div from "@jumbo/shared/Div";
import pluginServices from 'app/services/plugin-services';
import JumboCardQuick from '@jumbo/components/JumboCardQuick';
import JumboScrollbar from '@jumbo/components/JumboScrollbar';
import ConfirmDialog from 'app/shared/widgets/ConfirmDialog';
import companyServices from 'app/services/company-services';
import useJumboAuth from '@jumbo/hooks/useJumboAuth';
import PluginListItem from './PluginListItem';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip
} from "recharts";

const formatQty = (value) => Number(value || 0).toLocaleString('es-CL');

const formatDateTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString('es-CL');
};

// Colores y logo del boton "Conectar" de cada integracion con OAuth. Mercado Libre
// queda como default porque fue el primero y los campos viejos no traen `brand`.
const BRAND_BUTTONS = Object.freeze({
  mercadolibre: {
    background: "#ffe600",
    hover: "#ffd600",
    color: "#333",
    logo: {
      src: "https://http2.mlstatic.com/frontend-assets/ui-navigation/5.21.11/mercadolibre/logo__large_plus.png",
      alt: "Mercado Libre",
      style: { width: 84, height: 24 }
    }
  },
  shopify: {
    background: "#008060",
    hover: "#006e52",
    color: "#fff",
    logo: { src: "/images/icons/shopify.svg", alt: "Shopify", style: { width: 22, height: 22 } }
  }
});

const MERCADO_LIBRE_LIMIT_COLORS = Object.freeze({
  limit: '#F59E0B',
  used: '#FBBF24',
  overBy: '#DC2626',
  remaining: '#D1D5DB'
});

// Bsale no tiene OAuth: el usuario copia el access_token del panel a mano, asi que
// lo unico que se puede hacer por el es llevarlo al lugar exacto de donde sacarlo.
const BSALE_PANEL_URL = 'https://account.bsale.dev/';
const BSALE_BLUE = '#0A6ECF';
const BSALE_BLUE_DARK = '#0857A8';

const extractNumericCapabilityLimit = (capability) => {
  if (!capability) return null;

  const candidates = [capability.limit, capability.raw];

  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined) continue;

    if (typeof candidate === 'number' && Number.isFinite(candidate) && candidate > 0) {
      return Math.trunc(candidate);
    }

    if (typeof candidate === 'object') {
      const maxValue = candidate.max;
      if (Number.isFinite(Number(maxValue)) && Number(maxValue) > 0) {
        return Math.trunc(Number(maxValue));
      }
      continue;
    }

    if (typeof candidate === 'string') {
      const normalized = candidate.trim();
      if (!normalized) continue;

      if (/^\d+$/.test(normalized)) return Number(normalized);

      const rangeMatch = normalized.match(/(\d+)\s*-\s*(\d+)/);
      if (rangeMatch) return Number(rangeMatch[2]);

      const embeddedNumber = normalized.match(/(\d+)/);
      if (embeddedNumber) return Number(embeddedNumber[1]);
    }
  }

  return null;
};

const MercadoLibreLimitTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;

  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 1.5,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 8px 24px rgba(0,0,0,0.08)'
      }}
    >
      {payload.map((item) => (
        <Typography key={item.name} variant="body2" sx={{ color: item.color }}>
          {item.name}: {formatQty(item.value)}
        </Typography>
      ))}
    </Box>
  );
};

const VaryingField = (props) => {
  if (props.field.type === 'string' || props.field.type === 'password') {
    return (
      <TextField
        fullWidth
        type={props.field.type === 'password' ? 'password' : 'text'}
        required={props.field.required}
        label={props.field.label}
        value={props.value || ''}
        onChange={(e) => props.onChange(e)}
        helperText={props.field.help || ''}
        variant="outlined"
        sx={{ mt: 2 }}
      />
    )
  }

  if (props.field.type === 'button') {
    const brand = BRAND_BUTTONS[props.field.brand] || BRAND_BUTTONS.mercadolibre;
    return (
      <Button
        variant="contained"
        disabled={Boolean(props.field.disabled)}
        sx={{
          mt: 2,
          px: 3,
          backgroundColor: brand.background,
          color: brand.color,
          fontWeight: "bold",
          fontSize: "14px",
          borderRadius: "8px",
          boxShadow: "0px 2px 4px rgba(0,0,0,0.2)",
          "&:hover": { backgroundColor: brand.hover }
        }}
        startIcon={<img src={brand.logo.src} alt={brand.logo.alt} style={brand.logo.style} />}
        onClick={() => {
          if (typeof props.field.onClick === "function") {
            props.field.onClick();
            return;
          }

          const API_BASE = process.env.REACT_APP_HOST || "https://dev-api.haulers.cl";
          const origin = encodeURIComponent(window.location.href);
          window.location.href = `${API_BASE}/api/v1/auth/mercadolibre?origin=${origin}`;
        }}
      >
        {props.field.label}
      </Button>
    )
  }
};

// Valores que el cliente tiene que pegar en el Dev Dashboard de Shopify. Son
// de solo lectura: el campo está para poder copiarlos, no para editarlos.
const ShopifyCopyValue = ({ label, value, onCopy }) => (
  <Div sx={{ mb: 1.25 }}>
    <Typography
      variant="caption"
      sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: '.08em', color: 'text.secondary', mb: 0.25 }}
    >
      {label}
    </Typography>
    <Stack direction="row" spacing={1} alignItems="center">
      <TextField
        size="small"
        fullWidth
        value={value}
        InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 12.5 } }}
        onFocus={(e) => e.target.select()}
      />
      <Button size="small" variant="outlined" sx={{ flexShrink: 0 }} onClick={() => onCopy(value)}>
        Copiar
      </Button>
    </Stack>
  </Div>
);

const PluginsIndex = () => {
  const { authUser } = useJumboAuth();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [pluginsList, setPluginsList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [listLoading, setListLoading] = useState(false);
  const [currentPlugin, setCurrentPlugin] = useState(null);
  const [currentCompanyPlugin, setCurrentCompanyPlugin] = useState(null);

  const [companyPlugins, setCompanyPlugins] = useState([]);
  const [companyPluginsLoading, setCompanyPluginsLoading] = useState(false);

  const [pluginInToggleInstallation, setPluginInToggleInstallation] = useState(null);
  const [pluginInToggleActivation, setPluginInToggleActivation] = useState(null);

  const [pluginConfig, setPluginConfig] = useState({});
  const [saving, setSaving] = useState(false);

  // Snack de Mercado Libre
  const [meliSnack, setMeliSnack] = useState({ open: false, severity: "info", text: "" });
  const [meliAccounts, setMeliAccounts] = useState([]);
  const [meliLoading, setMeliLoading] = useState(false);
  const [meliSavingFull, setMeliSavingFull] = useState(false);
  const [meliLimitModal, setMeliLimitModal] = useState({
    open: false,
    message: '',
    used: 0,
    limit: 0,
    overBy: 0
  });

  // Modal de confirmación
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    plugin: null,
    action: null, // 'activate' o 'deactivate'
  });

  const [confirmDisconnectDialog, setConfirmDisconnectDialog] = useState({
      open: false,
      accountId: null,
  });

  // Dominio de la tienda Shopify pendiente de desconectar, o null.
  const [confirmDisconnectShop, setConfirmDisconnectShop] = useState(null);

  // Shopify state
  const [shopifyShops, setShopifyShops] = useState([]);
  const [shopifyLoading, setShopifyLoading] = useState(false);
  const [shopifyCredsSet, setShopifyCredsSet] = useState(false);
  const [shopifyClientId, setShopifyClientId] = useState('');
  const [shopifyClientSecret, setShopifyClientSecret] = useState('');
  const [shopifySavingCreds, setShopifySavingCreds] = useState(false);
  // Primeros y ultimos caracteres del Client ID guardado, solo para el equipo.
  const [shopifyClientIdHint, setShopifyClientIdHint] = useState(null);
  // { fulfill_on_prepared, push_trigger_state, notify_customer_on_fulfillment, mark_delivered, mark_delivered_scope }
  const [shopifySettings, setShopifySettings] = useState(null);
  const [shopifySavingSettings, setShopifySavingSettings] = useState(false);
  // 'shopify', 'bsale' o null: por donde entran hoy los pedidos de Shopify.
  const [shopifyOrdersSource, setShopifyOrdersSource] = useState(null);

  // Bsale state. Una sola cuenta por company, asi que es un objeto y no una lista.
  const [bsaleConnection, setBsaleConnection] = useState(null);
  const [bsaleLoading, setBsaleLoading] = useState(false);
  const [bsaleToken, setBsaleToken] = useState('');
  const [bsaleConnecting, setBsaleConnecting] = useState(false);
  const [bsaleError, setBsaleError] = useState(null);
  const [bsaleDocTypeIds, setBsaleDocTypeIds] = useState([]);
  const [bsaleSavingDocTypes, setBsaleSavingDocTypes] = useState(false);
  // { "119": "mercadolibre" } y { mercadolibre: "direct" }
  const [bsaleChannelMap, setBsaleChannelMap] = useState({});
  const [bsaleOwners, setBsaleOwners] = useState({});
  const [bsaleSavingChannels, setBsaleSavingChannels] = useState(false);

  const [confirmSaveDialog, setConfirmSaveDialog] = useState({
    open: false,
  });

  // Equipo de Kargotools: superadmin con correo @kargotools.com, el mismo criterio que el backend
  // (global_superadmin?). Solo ellos configuran que importa Relbase.
  const isKargotools = (authUser?.profiles || []).some((p) => p.name === "superadmin")
    && /@kargotools\.com$/i.test(authUser?.email || "");

  const mercadoLibreCapability = authUser?.current_company?.subscription_capabilities?.mercadoLibre || {};
  const mercadoLibreEnabled = Boolean(mercadoLibreCapability?.enabled);
  const mercadoLibreAccountsLimit = extractNumericCapabilityLimit(mercadoLibreCapability);

  const closeMeliLimitModal = () => {
    setMeliLimitModal({ open: false, message: '', used: 0, limit: 0, overBy: 0 });
  };

  const openMeliLimitModal = ({ message, used, limit }) => {
    const safeUsed = Number(used || 0);
    const safeLimit = Number(limit || 0);
    setMeliLimitModal({
      open: true,
      message: message || 'Alcanzaste el limite de cuentas de Mercado Libre de tu plan actual.',
      used: safeUsed,
      limit: safeLimit,
      overBy: Math.max(safeUsed - safeLimit, 0)
    });
  };

  const buildMeliLimitChartData = (used, limit) => {
    const safeUsed = Number(used || 0);
    const safeLimit = Number(limit || 0);
    const overBy = Math.max(safeUsed - safeLimit, 0);

    if (safeLimit > 0 && overBy > 0) {
      return [
        { name: 'Límite plan', value: safeLimit, color: MERCADO_LIBRE_LIMIT_COLORS.limit },
        { name: 'Sobreconsumo', value: overBy, color: MERCADO_LIBRE_LIMIT_COLORS.overBy }
      ];
    }

    if (safeLimit > 0) {
      return [
        { name: 'Usado', value: safeUsed, color: MERCADO_LIBRE_LIMIT_COLORS.used },
        { name: 'Disponible', value: Math.max(safeLimit - safeUsed, 0), color: MERCADO_LIBRE_LIMIT_COLORS.remaining }
      ];
    }

    return [{ name: 'Usado', value: safeUsed, color: MERCADO_LIBRE_LIMIT_COLORS.used }];
  };

  const redirectToMercadoLibreOauth = () => {
    const API_BASE = process.env.REACT_APP_HOST || "https://dev-api.haulers.cl";
    const origin = encodeURIComponent(window.location.href);
    window.location.href = `${API_BASE}/api/v1/auth/mercadolibre?origin=${origin}`;
  };

  const handleConnectMercadoLibreAccount = () => {
    if (meliLoading) return;

    if (!mercadoLibreEnabled) {
      setMeliSnack({
        open: true,
        severity: "warning",
        text: "Tu plan actual no incluye integración con cuentas de Mercado Libre.",
      });
      return;
    }

    const used = Array.isArray(meliAccounts) ? meliAccounts.length : 0;
    const limit = Number(mercadoLibreAccountsLimit || 0);

    if (limit > 0 && used >= limit) {
      openMeliLimitModal({
        used,
        limit,
        message: 'Ya alcanzaste el maximo de cuentas de Mercado Libre permitidas por tu plan.'
      });
      return;
    }

    redirectToMercadoLibreOauth();
  };

  useEffect(() => {
    if (listLoading) loadPlugins();
    if (companyPluginsLoading) loadCompanyPlugins();
  }, [listLoading, companyPluginsLoading]);

  useEffect(() => {
    setCompanyPluginsLoading(true);
    setListLoading(true);
  }, []);

  useEffect(() => {
    if (currentPlugin?.slug === "mercado-libre") {
      setMeliLoading(true);
      pluginServices.getMeliAccounts()
        .then((data) => {
          if (data.ok) setMeliAccounts(Array.isArray(data.accounts) ? data.accounts : []);
        })
        .catch(() => { })
        .finally(() => setMeliLoading(false));
    }
    if (currentPlugin?.slug === "shopify") {
      setShopifyLoading(true);
      setShopifyClientId('');
      setShopifyClientSecret('');
      pluginServices.getShopifyShops()
        .then((data) => {
          setShopifyShops(Array.isArray(data?.shops) ? data.shops : []);
          setShopifyCredsSet(Boolean(data?.credentials_set));
          setShopifyClientIdHint(data?.client_id_hint || null);
          setShopifySettings(data?.settings || null);
          setShopifyOrdersSource(data?.orders_source || null);
        })
        .catch(() => {
          setShopifyShops([]);
          setShopifyCredsSet(false);
          setShopifyClientIdHint(null);
          setShopifySettings(null);
          setShopifyOrdersSource(null);
        })
        .finally(() => setShopifyLoading(false));
    }
    if (currentPlugin?.slug === "bsale") {
      // El token no vuelve nunca del backend, asi que el campo arranca vacio y lo
      // que se muestra del token guardado es la pista de los ultimos caracteres.
      setBsaleToken('');
      setBsaleError(null);
      setBsaleLoading(true);
      pluginServices.getBsaleConnection()
        .then((data) => {
          setBsaleConnection(data);
          setBsaleDocTypeIds(Array.isArray(data?.document_type_ids) ? data.document_type_ids : []);
          applyBsaleChannels(data);
        })
        .catch((err) => {
          setBsaleConnection(null);
          setBsaleError(err?.response?.data?.error || 'No se pudo leer el estado de la conexion');
        })
        .finally(() => setBsaleLoading(false));
    }
  }, [currentPlugin]);

  const handleConnectShopifyShop = async () => {
    const raw = window.prompt('Dominio de tu tienda Shopify (ej: tutienda.myshopify.com):');
    if (!raw) return;
    const shop = raw.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '');
    if (!shop) return;
    try {
      const data = await pluginServices.getShopifyInstallUrl(shop);
      if (data?.authorize_url) {
        window.location.href = data.authorize_url;
      } else {
        enqueueSnackbar('No se pudo generar la URL de autorización', { variant: 'error' });
      }
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Error al iniciar OAuth de Shopify';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  const handleCopyShopifyValue = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      enqueueSnackbar('Copiado', { variant: 'success' });
    } catch (err) {
      // Sin permiso de portapapeles (pasa en http o en algunos navegadores) el
      // campo igual se puede seleccionar y copiar a mano.
      enqueueSnackbar('No se pudo copiar: selecciona el texto y copia con Ctrl+C', { variant: 'info' });
    }
  };

  const handleSaveShopifyCredentials = async () => {
    const clientId = shopifyClientId.trim();
    const clientSecret = shopifyClientSecret.trim();
    if (!clientId || !clientSecret) {
      enqueueSnackbar('Faltan el Client ID o el Client secret', { variant: 'warning' });
      return;
    }
    setShopifySavingCreds(true);
    try {
      await pluginServices.saveShopifyCredentials({ client_id: clientId, client_secret: clientSecret });
      setShopifyCredsSet(true);
      setShopifyClientId('');
      setShopifyClientSecret('');
      enqueueSnackbar('Credenciales guardadas, ya puedes conectar la tienda', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.error || 'No se pudieron guardar las credenciales', { variant: 'error' });
    }
    setShopifySavingCreds(false);
  };

  // Guarda un cambio a la vez y deja en pantalla lo que contesto el backend.
  const handleSaveShopifySetting = async (cambio) => {
    if (shopifySavingSettings) return;
    setShopifySavingSettings(true);
    try {
      const data = await pluginServices.saveShopifySettings(cambio);
      setShopifySettings(data?.settings || null);
      enqueueSnackbar('Configuración guardada', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.error || 'No se pudo guardar la configuración', { variant: 'error' });
    }
    setShopifySavingSettings(false);
  };

  const handleDisconnectShopifyShop = async (domain) => {
    setConfirmDisconnectShop(null);
    try {
      await pluginServices.disconnectShopifyShop(domain);
      setShopifyShops((prev) => prev.filter((s) => s.shop_domain !== domain));
      enqueueSnackbar('Tienda desconectada', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.error || 'Error al desconectar', { variant: 'error' });
    }
  };

  useEffect(() => {
    // data_fields puede no ser un Hash (ej: shopify lo tiene como Array) y puede no
    // declarar todas las claves presentes en data_values, así que no se indexa a ciegas.
    const dataFields = currentCompanyPlugin?.plugin?.data_fields;
    const newPC = { ...(currentCompanyPlugin?.data_values || {}) };
    _.each(newPC, (v, k) => {
      const field = _.isPlainObject(dataFields) ? dataFields[k] : undefined;
      if (field?.type === 'password') {
        newPC[k] = '';
      }
    });
    setPluginConfig(newPC);
    setCurrentPlugin(currentCompanyPlugin?.plugin);
  }, [currentCompanyPlugin]);

  // Detecta query params (Mercado Libre)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const meli = params.get("meli");
    const reason = params.get("reason");
    const feature = params.get("feature");
    const usedParam = Number(params.get("used") || 0);
    const limitParam = Number(params.get("limit") || 0);

    if (meli) {
      if (meli === "connected") {
        setMeliSnack({
          open: true,
          severity: "success",
          text: "¡Mercado Libre conectado correctamente!",
        });
        setCompanyPluginsLoading(true);
        setListLoading(true);
      } else if (meli === "error") {
        if (reason === "subscription_limit_reached" && feature === "mercadoLibre") {
          openMeliLimitModal({
            used: usedParam,
            limit: limitParam,
            message: 'Ya alcanzaste el maximo de cuentas de Mercado Libre permitidas por tu plan.'
          });
          setMeliSnack({
            open: true,
            severity: "warning",
            text: "No se pudo conectar: alcanzaste el limite de cuentas Mercado Libre de tu plan.",
          });
        } else if (reason === "subscription_feature_disabled" && feature === "mercadoLibre") {
          setMeliSnack({
            open: true,
            severity: "warning",
            text: "Tu plan actual no incluye integración con cuentas de Mercado Libre.",
          });
        } else {
          setMeliSnack({
            open: true,
            severity: "error",
            text: `No se pudo conectar con Mercado Libre${reason ? `: ${reason}` : ""}`,
          });
        }
      }

      params.delete("meli");
      params.delete("reason");
      params.delete("feature");
      params.delete("used");
      params.delete("limit");
      const newQs = params.toString();
      const newUrl = `${window.location.pathname}${newQs ? `?${newQs}` : ""}${window.location.hash || ""}`;
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  const buildParams = () => {
    let par = [];
    let pos = 0;
    par.push(encodeURI(`q[m]=and`));
    if (searchQuery && searchQuery !== 0) {
      par.push(encodeURI(`q[g][${pos}][data_cont]=${searchQuery}`));
      pos += 1;
    }
    par.push(`page=${currentPage}`);
    par = _.join(par, '&');
    return `?${par}`;
  };

  const handleSearchQueryChange = (newVal) => {
    setCurrentPage(1);
    setSearchQuery(newVal);
    setListLoading(true);
  };

  const debounceSearchQueryChange = _.debounce(handleSearchQueryChange, 450);

  const loadPlugins = () => {
    pluginServices.getPlugins(buildParams())
      .then(data => {
        setPluginsList(data.data);
        setTotalPages(data.meta.total_pages);
      })
      .catch(() => { })
      .finally(() => setListLoading(false));
  };

  const loadCompanyPlugins = () => {
    if (authUser) {
      companyServices.getCompanyPlugins(authUser?.current_company?.id)
        .then(data => setCompanyPlugins(data))
        .catch(() => { })
        .finally(() => setCompanyPluginsLoading(false));
    }
  };

  const confirmTogglePluginActivation = () => {
    if (!confirmDialog.plugin) return;

    setPluginInToggleActivation(confirmDialog.plugin.id);
    setCurrentCompanyPlugin(null);

    pluginServices.togglePluginActivation(confirmDialog.plugin.id)
      .then(() => {
        setListLoading(true);
        setCompanyPluginsLoading(true);
      })
      .catch(() => { })
      .finally(() => {
        setPluginInToggleActivation(null);
        setConfirmDialog({ open: false, plugin: null, action: null });
      });
  };

  const openConfirmDialog = (plugin, isActive) => {
    setConfirmDialog({
      open: true,
      plugin,
      action: isActive ? 'deactivate' : 'activate',
    });
  };

  const renderInstalledPluginsList = (company_plugins, _loading = false) => {
    if (company_plugins) {
      return (
        <List disablePadding>
          {_.map(company_plugins, (cp, i) => {
            if (!cp.plugin) {
              console.warn("CompanyPlugin inválido:", cp);
              return <Typography key={i} color="error"></Typography>;
            }

            return (
              <PluginListItem
                key={cp.plugin.id || i}
                plugin={cp.plugin}
                enabled={cp.enabled}
                inLoading={pluginInToggleActivation}
                enableConfigButton={cp.enabled}
                onText="Activado"
                offText="No activado"
                onToggle={(plug) => openConfirmDialog(plug, cp.enabled)}
                onConfig={(plug) => setCurrentCompanyPlugin(cp)}
              />
            );
          })}
        </List>
      );
    } else if (!_loading) {
      return <Typography textAlign="center">No existen datos</Typography>;
    }
  };

  const renderPluginsList = (plugins, _loading = false) => {
    if (plugins) {
      return (
        <List disablePadding>
          {_.map(plugins, (p, i) => {
            const companyPlugin = _.find(companyPlugins, (cp) => cp.plugin && cp.plugin.id === p.id);
            return (
              <PluginListItem
                key={p.id || i}
                plugin={p}
                enabled={!!companyPlugin}
                inLoading={pluginInToggleInstallation}
                onText="Instalado"
                offText="No instalado"
                onToggle={(plug) => togglePluginInstallation(plug)}
              />
            );
          })}
        </List>
      );
    } else if (!_loading) {
      return <Typography textAlign="center">No existen datos</Typography>;
    }
  };

  const togglePluginInstallation = (plugin) => {
    setPluginInToggleInstallation(plugin.id);
    setCurrentCompanyPlugin(null);
    pluginServices.togglePluginInstallation(plugin.id)
      .then(() => {
        setListLoading(true);
        setCompanyPluginsLoading(true);
      })
      .catch(() => { })
      .finally(() => setPluginInToggleInstallation(null));
  };

  const savePluginConfig = () => {
    if (currentPlugin) {
      setSaving(true);
      pluginServices.saveConfig(currentPlugin.id, { data_values: pluginConfig })
        .then(() => { })
        .catch(() => { })
        .finally(() => setSaving(false));
    }
  };

  // Si los pedidos Full de Mercado Libre se esconden. El switch dice "Ocultar" para que
  // encendido sea esconderlos: con "Mostrar" se leia al reves y quedaba prendido creyendo
  // que no se traian. Se sigue guardando show_full_orders (el backend lee esa clave); sin
  // la clave guardada se ven, que es lo que hubo siempre.
  const meliHideFullOrders = pluginConfig?.show_full_orders === false;

  const handleToggleMeliFullOrders = (hide) => {
    if (!currentPlugin || meliSavingFull) return;
    const pluginId = currentPlugin.id;
    const show = !hide;

    setMeliSavingFull(true);
    pluginServices.saveConfig(pluginId, { data_values: { show_full_orders: show } })
      .then(() => {
        setPluginConfig((prev) => ({ ...prev, show_full_orders: show }));
        // Para que al volver a abrir la configuracion no aparezca el valor anterior.
        setCompanyPlugins((prev) => (Array.isArray(prev) ? prev : []).map((cp) => (
          cp.plugin_id === pluginId
            ? { ...cp, data_values: { ...(cp.data_values || {}), show_full_orders: show } }
            : cp
        )));
        enqueueSnackbar(
          hide ? 'Los pedidos Full ya no se mostrarán' : 'Los pedidos Full se volverán a mostrar',
          { variant: 'success' }
        );
      })
      .catch(() => enqueueSnackbar('No se pudo guardar la configuración', { variant: 'error' }))
      .finally(() => setMeliSavingFull(false));
  };

  const handleDisconnectAccount = (id) => {
      setConfirmDisconnectDialog({
          open: true,
          accountId: id
      });
  };

  const confirmDisconnectAccount = () => {
      const id = confirmDisconnectDialog.accountId;
      if (!id) return;

      pluginServices.disconnectMeliAccount(id)
          .then((resp) => {
              if (resp.ok) {
                  setMeliAccounts(prev => prev.filter(acc => acc.id !== id));
                  setMeliSnack({
                      open: true,
                      severity: "success",
                      text: "Cuenta desconectada correctamente."
                  });
              } else {
                  setMeliSnack({
                      open: true,
                      severity: "error",
                      text: "No se pudo desconectar la cuenta."
                  });
              }
          })
          .catch(() =>
              setMeliSnack({
                  open: true,
                  severity: "error",
                  text: "Error al desconectar la cuenta."
              })
          )
          .finally(() => {
              setConfirmDisconnectDialog({
                  open: false,
                  accountId: null
              });
          });
  };

  // El backend prueba el token contra Bsale antes de guardarlo: si contesta 422 el
  // token no sirve y el que estaba guardado sigue intacto.
  const handleConnectBsale = async () => {
    const token = bsaleToken.trim();
    if (!token) return;

    setBsaleConnecting(true);
    setBsaleError(null);
    try {
      const data = await pluginServices.connectBsale(token);
      setBsaleConnection(data);
      setBsaleDocTypeIds(Array.isArray(data?.document_type_ids) ? data.document_type_ids : []);
      setBsaleToken('');
      enqueueSnackbar('Conexion con Bsale establecida', { variant: 'success' });
    } catch (err) {
      const msg = err?.response?.data?.error || 'No se pudo conectar con Bsale';
      setBsaleError(msg);
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setBsaleConnecting(false);
    }
  };

  // El backend manda el dueño efectivo (el que aplica hoy) y el explicito (el que
  // alguien eligio). El selector arranca en el efectivo, asi lo que se ve es lo que
  // esta pasando aunque nadie haya elegido nada todavia.
  const applyBsaleChannels = (data) => {
    setBsaleChannelMap(data?.document_type_channels || {});
    setBsaleOwners(
      (data?.channels || []).reduce((acc, channel) => {
        acc[channel.channel] = channel.explicit_owner || channel.owner;
        return acc;
      }, {})
    );
  };

  const setBsaleChannelForType = (id, channel) => {
    setBsaleChannelMap((current) => {
      const next = { ...current };
      // 'auto' es la ausencia de marca: deja que el backend detecte por documento.
      // 'own' si es una eleccion, para tapar una deteccion equivocada.
      if (channel === 'auto') delete next[id];
      else next[id] = channel;
      return next;
    });
  };

  const handleSaveBsaleChannels = async () => {
    setBsaleSavingChannels(true);
    try {
      const data = await pluginServices.saveBsaleChannels({ channel_owners: bsaleOwners });
      setBsaleConnection(data);
      applyBsaleChannels(data);
      enqueueSnackbar('Canales guardados', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.error || 'No se pudieron guardar los canales', { variant: 'error' });
    } finally {
      setBsaleSavingChannels(false);
    }
  };

  const toggleBsaleDocType = (id) => {
    setBsaleDocTypeIds((current) => (
      current.includes(id) ? current.filter((current_id) => current_id !== id) : [...current, id]
    ));
  };

  const handleSaveBsaleDocTypes = async () => {
    setBsaleSavingDocTypes(true);
    try {
      // Los dos viven en la misma lista, asi que se guardan juntos: que tipos se
      // sincronizan y a que canal pertenece cada uno.
      await pluginServices.saveBsaleDocumentTypes(bsaleDocTypeIds);
      const data = await pluginServices.saveBsaleChannels({ document_type_channels: bsaleChannelMap });
      setBsaleConnection(data);
      applyBsaleChannels(data);
      enqueueSnackbar('Tipos de documento guardados', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.error || 'No se pudieron guardar los tipos de documento', { variant: 'error' });
    } finally {
      setBsaleSavingDocTypes(false);
    }
  };

  const handleFieldChange = (key, field, value) => {
    if (key) {
      var newPluginConfig = { ...pluginConfig };
      newPluginConfig[key] = value;
      setPluginConfig(newPluginConfig);
    }
  };

  const renderPluginConfig = (plugin) => {
    if (plugin) {
      if (plugin.slug === "mercado-libre") {
        const usedAccounts = Array.isArray(meliAccounts) ? meliAccounts.length : 0;
        const hasLimit = Number(mercadoLibreAccountsLimit || 0) > 0;
        const isAtLimit = hasLimit && usedAccounts >= Number(mercadoLibreAccountsLimit);
        const mlCapabilityRaw = mercadoLibreCapability?.raw;

        return (
          <>
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              Cuentas activas
            </Typography>

            <Alert severity={!mercadoLibreEnabled ? "error" : isAtLimit ? "warning" : "info"} sx={{ mb: 2 }}>
              {!mercadoLibreEnabled
                ? `Tu plan actual no incluye cuentas de Mercado Libre${mlCapabilityRaw ? ` (${mlCapabilityRaw})` : ''}.`
                : hasLimit
                  ? `Cuentas conectadas: ${formatQty(usedAccounts)} / ${formatQty(mercadoLibreAccountsLimit)}`
                  : `Cuentas conectadas: ${formatQty(usedAccounts)} (sin límite definido en el plan)`}
            </Alert>

            {meliLoading && (
              <Div sx={{ textAlign: "center", my: 2 }}>
                <CircularProgress color="warning" />
              </Div>
            )}

            {!meliLoading && meliAccounts.length === 0 && (
              <Typography color="text.secondary">
                No hay cuentas conectadas actualmente.
              </Typography>
            )}

            {!meliLoading && meliAccounts.length > 0 && (
              <List disablePadding>
                {meliAccounts.map((acc) => (
                  <Stack
                    key={acc.id}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      borderBottom: "1px solid #eee",
                      py: 1,
                      px: 1
                    }}
                  >
                    <div>
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        {acc.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {acc.email}
                      </Typography>
                    </div>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => handleDisconnectAccount(acc.id)}
                    >
                      Cerrar sesión
                    </Button>
                  </Stack>
                ))}
              </List>
            )}

            <VaryingField
              field={{
                type: "button",
                label: "Conectar nueva cuenta Mercado Libre",
                disabled: meliLoading || !mercadoLibreEnabled,
                onClick: handleConnectMercadoLibreAccount
              }}
            />

            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
              Pedidos
            </Typography>
            <FormControlLabel
              sx={{ ml: 0, alignItems: 'flex-start' }}
              control={
                <Switch
                  checked={meliHideFullOrders}
                  disabled={meliSavingFull}
                  onChange={(e) => handleToggleMeliFullOrders(e.target.checked)}
                />
              }
              label={
                <Box sx={{ pt: 1 }}>
                  <Typography variant="body1">Ocultar pedidos Full</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Los pedidos Full los prepara y despacha Mercado Libre desde su bodega. Encendido,
                    se siguen importando pero no aparecen en Pedidos, Planificación ni en los indicadores.
                  </Typography>
                </Box>
              }
            />
          </>
        );
      }
      if (plugin.slug === "shopify") {
        const tiendaConectada = shopifyShops.length > 0;
        const REDIRECT_URL = 'https://api.haulers.cl/api/v1/shopify/oauth/callback';
        const SCOPES = 'read_orders,write_orders,read_fulfillments,write_fulfillments,'
          + 'read_assigned_fulfillment_orders,write_assigned_fulfillment_orders,'
          + 'write_merchant_managed_fulfillment_orders,read_products';

        return (
          <>
            {/* Los pasos solo mientras falte algo: una vez conectada la tienda
                estorban, y el que vuelve a esta pantalla viene a otra cosa.
                La app la crea Kargotools en su Dev Dashboard, una por cliente, para
                poder sumarle permisos sin depender del cliente. Por eso crearla y
                pegar las credenciales lo ve solo el equipo, igual que el backend. */}
            {!tiendaConectada && !isKargotools && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                La app de Shopify la prepara el equipo de Haulers. Escríbenos a{' '}
                <a href="mailto:info@haulers.cl">info@haulers.cl</a> con el dominio <code>.myshopify.com</code> de
                tu tienda. Cuando te avisemos que está lista, presiona el botón de más abajo y aprueba los
                permisos que te muestra Shopify.
              </Typography>
            )}
            {!tiendaConectada && isKargotools && (
              <Div sx={{ mt: 2 }}>
                <Typography variant="h6" sx={{ mb: 0.5 }}>
                  1. Crea la app en Shopify
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  En la organización <b>Haulers</b> del{' '}
                  <a href="https://dev.shopify.com/dashboard/217385230/apps" target="_blank" rel="noopener noreferrer">
                    Dev Dashboard
                  </a>
                  , haz <b>Crear app</b> y llámala <b>Haulers – {authUser?.current_company?.name || 'empresa'}</b>. En
                  la versión pega estos tres valores, activa <b>flujo de instalación heredado</b>
                  {' '}(<code>use_legacy_install_flow</code>) y presiona <b>Lanzar</b>: sin eso Shopify instala la app
                  por su cuenta y Haulers nunca recibe la conexión. Después, en <b>Panel general → Distribución →
                  Distribución personalizada</b>, escribe el dominio <code>.myshopify.com</code> del cliente. No uses
                  {' '}<b>Instalar app</b>: la instalación la hace el botón de más abajo.
                </Typography>

                <ShopifyCopyValue label="App URL" value="https://app.haulers.cl" onCopy={handleCopyShopifyValue} />
                <ShopifyCopyValue label="Allowed redirection URL" value={REDIRECT_URL} onCopy={handleCopyShopifyValue} />
                <ShopifyCopyValue label="Scopes (permisos)" value={SCOPES} onCopy={handleCopyShopifyValue} />

                <Typography variant="h6" sx={{ mt: 3, mb: 0.5 }}>
                  2. Pega las credenciales de esa app
                  {shopifyCredsSet && (
                    <Chip label="Guardadas" size="small" color="success" variant="outlined" sx={{ ml: 1 }} />
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Están en <b>Credenciales</b>, en el panel general de la app que acabas de crear.
                  {shopifyClientIdHint && (
                    <>
                      {' '}El Client ID guardado es <code>{shopifyClientIdHint}</code>: si no es el de esa app,
                      pega el nuevo y se reemplaza.
                    </>
                  )}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-start">
                  <TextField
                    size="small"
                    fullWidth
                    label="Client ID"
                    value={shopifyClientId}
                    onChange={(e) => setShopifyClientId(e.target.value)}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label="Client secret"
                    type="password"
                    value={shopifyClientSecret}
                    onChange={(e) => setShopifyClientSecret(e.target.value)}
                  />
                  <LoadingButton
                    variant="contained"
                    loading={shopifySavingCreds}
                    onClick={handleSaveShopifyCredentials}
                    sx={{ flexShrink: 0 }}
                  >
                    Guardar
                  </LoadingButton>
                </Stack>

                <Typography variant="h6" sx={{ mt: 3, mb: 0.5 }}>
                  3. Conecta la tienda
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Con el botón de más abajo, desde la cuenta del cliente o desde la tuya si eres colaborador de
                  su tienda. Shopify pide aprobar los permisos; al volver, la tienda aparece acá. Si dice que el
                  link de instalación no es válido, falta <b>Lanzar</b> o la <b>distribución</b> del paso 1.
                </Typography>
              </Div>
            )}

            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
              Tiendas conectadas
            </Typography>

            {shopifyLoading && (
              <Div sx={{ textAlign: "center", my: 2 }}>
                <CircularProgress />
              </Div>
            )}

            {!shopifyLoading && shopifyShops.length === 0 && (
              <Typography color="text.secondary">
                No hay tiendas conectadas todavía.
              </Typography>
            )}

            {!shopifyLoading && shopifyShops.length > 0 && (
              <List disablePadding>
                {shopifyShops.map((s) => (
                  <Stack
                    key={s.shop_domain}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ borderBottom: "1px solid #eee", py: 1, px: 1 }}
                  >
                    <div>
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        {s.shop_domain}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Instalada: {s.installed_at}
                      </Typography>
                      {shopifyClientIdHint && (
                        <Typography variant="body2" color="text.secondary">
                          Client ID de la app: <code>{shopifyClientIdHint}</code>
                        </Typography>
                      )}
                    </div>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => setConfirmDisconnectShop(s.shop_domain)}
                    >
                      Desconectar
                    </Button>
                  </Stack>
                ))}
              </List>
            )}

            <VaryingField
              field={{
                type: "button",
                label: "Conectar tienda Shopify",
                brand: "shopify",
                disabled: shopifyLoading,
                onClick: handleConnectShopifyShop
              }}
            />

            {/* Traer pedidos desde Shopify no se ofrece: prenderlo con Bsale tambien
                trayendolos duplica pedidos, asi que solo se muestra por donde entran. */}
            {tiendaConectada && shopifySettings && (
              <>
                <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                  Pedidos
                </Typography>
                {shopifyOrdersSource && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Los pedidos de Shopify entran a Haulers{' '}
                    {shopifyOrdersSource === 'shopify' ? 'directo desde Shopify' : 'a través de Bsale'}.
                  </Typography>
                )}
                <FormControlLabel
                  sx={{ ml: 0, alignItems: 'flex-start' }}
                  control={
                    <Switch
                      checked={Boolean(shopifySettings.fulfill_on_prepared)}
                      disabled={shopifySavingSettings}
                      onChange={(e) => handleSaveShopifySetting({ fulfill_on_prepared: e.target.checked })}
                    />
                  }
                  label={
                    <Box sx={{ pt: 1 }}>
                      <Typography variant="body1">Marcar como Preparado en Shopify</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Cuando el pedido está listo en bodega, Haulers lo deja como Preparado en Shopify.
                        Apagado, solo se le agrega la etiqueta <code>haulers:preparado</code>.
                      </Typography>
                    </Box>
                  }
                />
                {shopifySettings.fulfill_on_prepared && (
                  <Box sx={{ pl: 7, mt: 1 }}>
                    <TextField
                      select
                      size="small"
                      label="Cuándo"
                      value={shopifySettings.push_trigger_state || 'prepared'}
                      disabled={shopifySavingSettings}
                      onChange={(e) => handleSaveShopifySetting({ push_trigger_state: e.target.value })}
                      sx={{ minWidth: 260, mb: 1 }}
                    >
                      <MenuItem value="picked">Al terminar el pickeo</MenuItem>
                      <MenuItem value="prepared">Al quedar preparado</MenuItem>
                    </TextField>
                    <FormControlLabel
                      sx={{ ml: 0, alignItems: 'flex-start', display: 'flex' }}
                      control={
                        <Switch
                          checked={Boolean(shopifySettings.notify_customer_on_fulfillment)}
                          disabled={shopifySavingSettings}
                          onChange={(e) => handleSaveShopifySetting({ notify_customer_on_fulfillment: e.target.checked })}
                        />
                      }
                      label={
                        <Box sx={{ pt: 1 }}>
                          <Typography variant="body1">Avisar al comprador</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Shopify le manda al comprador el correo de que su pedido va en camino. Conviene
                            apagado: cuando se prepara, el pedido todavía no sale de la bodega.
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                )}

                <FormControlLabel
                  sx={{ ml: 0, mt: 2, alignItems: 'flex-start' }}
                  control={
                    <Switch
                      checked={Boolean(shopifySettings.mark_delivered)}
                      disabled={shopifySavingSettings}
                      onChange={(e) => handleSaveShopifySetting({ mark_delivered: e.target.checked })}
                    />
                  }
                  label={
                    <Box sx={{ pt: 1 }}>
                      <Typography variant="body1">Marcar como Entregado en Shopify</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Cuando el pedido se entrega, Haulers lo deja como Entregado en Shopify. Si Shopify ya
                        lo tiene entregado no hace nada. Según cómo tenga configuradas sus notificaciones la
                        tienda, Shopify puede avisarle al comprador.
                      </Typography>
                    </Box>
                  }
                />
                {shopifySettings.mark_delivered && (
                  <Box sx={{ pl: 7, mt: 1 }}>
                    {/* Con Blue Express integrado a Shopify, el courier ya publica su propio
                        entregado: por eso el default deja fuera lo que cierra un courier. */}
                    <TextField
                      select
                      size="small"
                      label="Qué entregas"
                      value={shopifySettings.mark_delivered_scope || 'haulers_app'}
                      disabled={shopifySavingSettings}
                      onChange={(e) => handleSaveShopifySetting({ mark_delivered_scope: e.target.value })}
                      sx={{ minWidth: 320 }}
                      helperText="Si tu courier ya actualiza Shopify (por ejemplo, Blue Express), deja solo la app de Haulers."
                    >
                      <MenuItem value="haulers_app">Solo las de la app de Haulers</MenuItem>
                      <MenuItem value="all">También las que cierra un courier</MenuItem>
                    </TextField>
                  </Box>
                )}
              </>
            )}
          </>
        );
      }
      if (plugin.slug === "bsale") {
        const connected = bsaleConnection?.connected === true;
        const hint = bsaleConnection?.token_hint;
        const connectedAt = formatDateTime(bsaleConnection?.connected_at);
        const lastEmissionAt = formatDateTime(bsaleConnection?.last_emission_at);
        const docTypes = bsaleConnection?.document_types || [];
        // La cuenta demo de Bsale devuelve sucursales repetidas y alguna sin nombre,
        // asi que se limpian y se cortan: el panel es para reconocer la empresa, no
        // para listar la casa matriz doce veces.
        const officeLabels = _.uniq(
          (bsaleConnection?.offices || [])
            .map((office) => _.compact([office.name, office.city]).join(' - '))
            .filter(Boolean)
        );
        const savedDocTypeIds = bsaleConnection?.document_type_ids || [];
        const docTypesChanged = _.xor(savedDocTypeIds, bsaleDocTypeIds).length > 0
          || !_.isEqual(bsaleConnection?.document_type_channels || {}, bsaleChannelMap);
        const channels = bsaleConnection?.channels || [];
        const ownersChanged = channels.some((channel) => (
          (bsaleOwners[channel.channel] || channel.owner) !== channel.owner
        ));

        return (
          <>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2, mb: 1 }}>
              <Typography variant="h6" sx={{ m: 0 }}>Sesion de Bsale</Typography>
              {bsaleLoading
                ? <CircularProgress size={20} />
                : <Chip
                    size="small"
                    label={connected ? "Conexion activa" : "Sin conectar"}
                    color={connected ? "success" : "default"}
                    variant={connected ? "filled" : "outlined"} />}
            </Stack>

            {/* Bsale no tiene OAuth, asi que el token se copia a mano. Lo unico que
                se puede hacer por el usuario es decirle exactamente de donde. */}
            <Alert severity="info" icon={false} sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Como conectar tu cuenta
              </Typography>
              <Typography variant="body2" component="div">
                1. Entra a Bsale con el boton de abajo e inicia sesion.<br />
                2. En el panel, abre Integraciones y copia el access token de la empresa que quieres sincronizar.<br />
                3. Pegalo aca y presiona Conectar.
              </Typography>
            </Alert>

            <Button
              variant="contained"
              href={BSALE_PANEL_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                px: 3,
                backgroundColor: BSALE_BLUE,
                color: "#fff",
                fontWeight: "bold",
                fontSize: "14px",
                borderRadius: "8px",
                boxShadow: "0px 2px 4px rgba(0,0,0,0.2)",
                "&:hover": { backgroundColor: BSALE_BLUE_DARK }
              }}
            >
              Abrir Bsale
            </Button>

            <TextField
              fullWidth
              type="password"
              label={connected ? "Reemplazar access token" : "Access token"}
              value={bsaleToken}
              onChange={(e) => setBsaleToken(e.target.value)}
              helperText="Se valida contra Bsale antes de guardarlo. Si no sirve, el token actual queda intacto."
              variant="outlined"
              sx={{ mt: 2 }}
            />

            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
              <LoadingButton
                loading={bsaleConnecting}
                disabled={bsaleConnecting || !bsaleToken.trim()}
                variant="contained"
                color="primary"
                size="small"
                sx={{ minWidth: 140 }}
                loadingPosition="end"
                onClick={handleConnectBsale}>
                {bsaleConnecting ? "Validando" : "Conectar"}
              </LoadingButton>
            </Stack>

            {bsaleError && (
              <Alert severity="error" sx={{ mt: 2 }}>{bsaleError}</Alert>
            )}

            {!bsaleLoading && bsaleConnection && !connected && !bsaleError && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {bsaleConnection.error || "No hay ningun token guardado todavia."}
              </Alert>
            )}

            {connected && (
              <Alert severity="success" sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Bsale respondio correctamente
                </Typography>
                <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
                  {hint && <>Token guardado: ••••{hint.last4} ({hint.length} caracteres)<br /></>}
                  {connectedAt && <>Conectado el {connectedAt}<br /></>}
                  {officeLabels.length > 0 && (
                    <>
                      Sucursales: {officeLabels.slice(0, 4).join(' | ')}
                      {officeLabels.length > 4 ? ` y ${officeLabels.length - 4} mas` : ''}
                      <br />
                    </>
                  )}
                  Pedidos importados: {formatQty(bsaleConnection.orders_imported)}
                  {lastEmissionAt && <> · Sincronizado hasta {lastEmissionAt}</>}
                </Typography>
              </Alert>
            )}

            {connected && (
              <>
                <Typography variant="h6" sx={{ mt: 3, mb: 0.5 }}>
                  Tipos de documento a sincronizar
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Bsale numera los tipos de documento por empresa, asi que estos ids son los de tu
                  cuenta. Si no eliges ninguno, la sincronizacion usa el default del plugin y puede
                  no traer nada.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  El canal de cada documento se detecta solo, uno por uno: si aparece en el checkout
                  de Mercado Libre o si trae numero de seguimiento de Shopify. El selector es para
                  corregir esa deteccion cuando un tipo es siempre de un canal, o siempre propio.
                </Typography>

                {/* Una cuenta de Bsale puede tener decenas de tipos, asi que la lista va
                    acotada y con scroll propio en vez de empujar el resto del dialogo. */}
                <Box
                  sx={{
                    maxHeight: 220,
                    overflowY: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    px: 1.5,
                    py: 0.5
                  }}
                >
                  <FormGroup>
                    {docTypes.map((type) => (
                      <Stack
                        key={type.id}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                      >
                        <FormControlLabel
                          sx={{ ml: 0, mr: 0, py: 0, flexGrow: 1 }}
                          control={
                            <Checkbox
                              size="small"
                              sx={{ py: 0.25 }}
                              checked={bsaleDocTypeIds.includes(type.id)}
                              onChange={() => toggleBsaleDocType(type.id)}
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ fontSize: 13 }}>
                              {type.name}
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                                #{type.id}
                              </Typography>
                            </Typography>
                          }
                        />
                        <TextField
                          select
                          size="small"
                          variant="standard"
                          value={bsaleChannelMap[type.id] || 'auto'}
                          onChange={(e) => setBsaleChannelForType(type.id, e.target.value)}
                          sx={{ minWidth: 150 }}
                          SelectProps={{ sx: { fontSize: 13 } }}
                        >
                          <MenuItem value="auto" sx={{ fontSize: 13 }}>Automatico</MenuItem>
                          <MenuItem value="own" sx={{ fontSize: 13 }}>Siempre venta propia</MenuItem>
                          <MenuItem value="mercadolibre" sx={{ fontSize: 13 }}>Siempre Mercado Libre</MenuItem>
                          <MenuItem value="shopify" sx={{ fontSize: 13 }}>Siempre Shopify</MenuItem>
                        </TextField>
                      </Stack>
                    ))}
                  </FormGroup>
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                  {formatQty(bsaleDocTypeIds.length)} de {formatQty(docTypes.length)} seleccionados
                </Typography>

                <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                  <LoadingButton
                    loading={bsaleSavingDocTypes}
                    disabled={bsaleSavingDocTypes || !docTypesChanged}
                    variant="contained"
                    color="primary"
                    size="small"
                    sx={{ minWidth: 140 }}
                    loadingPosition="end"
                    onClick={handleSaveBsaleDocTypes}>
                    {bsaleSavingDocTypes ? "Guardando" : "Guardar tipos"}
                  </LoadingButton>
                </Stack>

                <Typography variant="h6" sx={{ mt: 3, mb: 0.5 }}>
                  Canales de venta
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Una venta de Mercado Libre o Shopify puede llegar dos veces: por su integracion
                  directa y otra vez por el documento que Bsale emitio por esa misma venta. No se
                  deduplican solas, porque cada camino guarda su propio identificador. Elige quien
                  la trae y el otro lado la deja pasar.
                </Typography>

                {channels.map((channel) => {
                  const selectedOwner = bsaleOwners[channel.channel] || channel.owner;
                  const orphaned = selectedOwner === 'direct' && !channel.direct_available;

                  return (
                    <Box
                      key={channel.channel}
                      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, mb: 1 }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                        <div>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {channel.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Ultimos 30 dias: {formatQty(channel.orders_by_bsale)} por Bsale
                            {' · '}
                            {formatQty(channel.orders_by_direct)} por la integracion directa
                            {!channel.direct_available && ' · integracion directa sin conectar'}
                          </Typography>
                        </div>
                        <TextField
                          select
                          size="small"
                          variant="standard"
                          value={selectedOwner}
                          onChange={(e) => setBsaleOwners((current) => ({ ...current, [channel.channel]: e.target.value }))}
                          sx={{ minWidth: 190 }}
                          SelectProps={{ sx: { fontSize: 13 } }}
                        >
                          <MenuItem value="bsale" sx={{ fontSize: 13 }}>Los trae Bsale</MenuItem>
                          <MenuItem value="direct" sx={{ fontSize: 13 }}>Los trae {channel.label}</MenuItem>
                        </TextField>
                      </Stack>

                      {channel.conflict && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                          Este canal tiene pedidos entrando por los dos caminos en los ultimos 30 dias:
                          los que ya entraron duplicados hay que revisarlos a mano, elegir un dueño solo
                          evita los nuevos.
                        </Alert>
                      )}

                      {orphaned && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                          La integracion directa de {channel.label} no esta trayendo pedidos hoy. Si la
                          dejas como dueña, ese canal no va a entrar por ningun lado.
                        </Alert>
                      )}
                    </Box>
                  );
                })}

                <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                  <LoadingButton
                    loading={bsaleSavingChannels}
                    disabled={bsaleSavingChannels || !ownersChanged}
                    variant="contained"
                    color="primary"
                    size="small"
                    sx={{ minWidth: 140 }}
                    loadingPosition="end"
                    onClick={handleSaveBsaleChannels}>
                    {bsaleSavingChannels ? "Guardando" : "Guardar canales"}
                  </LoadingButton>
                </Stack>
              </>
            )}
          </>
        );
      }
      if (_.keys(plugin.data_fields).length > 0) {
        return (
          <>
            {_.map(plugin.data_fields, (field, key) => {
              if (field.show_in_form) {
                return (
                  <VaryingField
                    key={key}
                    field={field}
                    value={pluginConfig[key]}
                    help={field.help}
                    onChange={(e) => handleFieldChange(key, field, e.target.value)}
                  />
                );
              }
            })}
            <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ mt: 2 }}>
              <LoadingButton
                loading={saving}
                variant="contained"
                color="primary"
                size='small'
                sx={{ minWidth: 140 }}
                loadingPosition="end"
                disabled={saving}
                onClick={() => setConfirmSaveDialog({ open: true })}>
                {saving ? "Guardando" : "Guardar"}
              </LoadingButton>
            </Stack>
            {plugin.slug === "relbase-sale-notes" && isKargotools && currentCompanyPlugin?.id && (
              <>
                <Typography variant="h6" sx={{ mt: 3, mb: 0.5 }}>
                  Importación
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Qué documentos y de qué canales entran como pedidos, los retiros, los avisos de
                  anulación y la primera sincronización. Solo lo ve el equipo de Kargotools.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate(`/plugins/relbase/${currentCompanyPlugin.id}`)}>
                  Configurar qué se importa
                </Button>
              </>
            )}
          </>
        );
      } else {
        return <Typography sx={{ mt: 2 }}>Este plugin no posee parámetros para configurar</Typography>;
      }
    }
  };

  const renderMeliLimitModal = () => {
    const used = Number(meliLimitModal.used || 0);
    const limit = Number(meliLimitModal.limit || 0);
    const overBy = Number(meliLimitModal.overBy || 0);
    const chartData = buildMeliLimitChartData(used, limit);
    const chartTotal = chartData.reduce((acc, entry) => acc + Number(entry.value || 0), 0);

    return (
      <Dialog
        open={meliLimitModal.open}
        onClose={closeMeliLimitModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Límite de cuentas Mercado Libre alcanzado</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body1">
              {meliLimitModal.message}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Uso actual: {formatQty(used)} / {formatQty(limit)} cuentas.
            </Typography>
            {overBy > 0 && (
              <Typography variant="body2" sx={{ color: MERCADO_LIBRE_LIMIT_COLORS.overBy, fontWeight: 600 }}>
                Sobreconsumo: {formatQty(overBy)} cuentas.
              </Typography>
            )}

            <Box sx={{ height: 260, position: 'relative' }}>
              {chartTotal > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={54}
                        outerRadius={84}
                        paddingAngle={2}
                      >
                        {chartData.map((entry) => (
                          <Cell key={entry.name} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip content={<MercadoLibreLimitTooltip />} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none'
                    }}
                  >
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {formatQty(used)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      cuentas conectadas
                    </Typography>
                  </Box>
                </>
              ) : (
                <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                  <Typography variant="body2" color="text.secondary">
                    Sin datos de consumo.
                  </Typography>
                </Stack>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMeliLimitModal}>Aceptar</Button>
          <Button
            variant="contained"
            onClick={() => {
              closeMeliLimitModal();
              navigate('/subscriptions');
            }}
          >
            Ver suscripciones
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <JumboCardQuick title="Instalados" sx={{ mb: 2 }}>
          <JumboScrollbar autoHeight autoHeightMin={300} style={{ paddingRight: 16 }}>
            <Div sx={{ display: companyPluginsLoading ? 'block' : 'none', textAlign: 'center', my: 5 }}>
              <CircularProgress color="warning" />
            </Div>
            {renderInstalledPluginsList(companyPlugins, companyPluginsLoading)}
          </JumboScrollbar>
        </JumboCardQuick>

        <JumboCardQuick title="Disponibles" sx={{ mb: 2 }}>
          <JumboScrollbar autoHeight autoHeightMin={300} style={{ paddingRight: 16 }}>
            <Div sx={{ display: listLoading ? 'block' : 'none', textAlign: 'center', my: 5 }}>
              <CircularProgress color="warning" />
            </Div>
            {renderPluginsList(pluginsList, listLoading)}
          </JumboScrollbar>
        </JumboCardQuick>
      </Grid>

      <Grid item xs={12} md={6}>
        {currentPlugin && (
          <JumboCardQuick title="Configuración" sx={{ mb: 2 }}>
            <Typography variant="h5">{currentPlugin.name}</Typography>
            {renderPluginConfig(currentPlugin)}
          </JumboCardQuick>
        )}
      </Grid>

      <Snackbar
        open={meliSnack.open}
        autoHideDuration={5000}
        onClose={() => setMeliSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={meliSnack.severity}
          variant="filled"
          onClose={() => setMeliSnack(s => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {meliSnack.text}
        </Alert>
      </Snackbar>

      {renderMeliLimitModal()}

      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.action === 'activate' ? "Activar integración" : "Desactivar integración"}
        message={
          <>
            ¿Estás seguro que deseas {confirmDialog.action === 'activate' ? 'activar' : 'desactivar'} la integración <strong>{confirmDialog.plugin?.name}</strong>?
          </>
        }
        confirmText={confirmDialog.action === 'activate' ? "Activar" : "Desactivar"}
        onClose={() => setConfirmDialog({ open: false, plugin: null, action: null })}
        onConfirm={confirmTogglePluginActivation}
      />

      <ConfirmDialog
        open={confirmSaveDialog.open}
        title="Confirmar guardado"
        message="¿Deseas guardar esta configuración? Esto puede incluir credenciales sensibles como contraseñas."
        confirmText="Guardar"
        onClose={() => setConfirmSaveDialog({ open: false })}
        onConfirm={() => {
          setConfirmSaveDialog({ open: false });
          savePluginConfig();
        }}
      />

      <ConfirmDialog
          open={confirmDisconnectDialog.open}
          title="Desconectar cuenta"
          message={
            <>
              ¿Estás seguro que deseas desconectar esta cuenta de <strong>Mercado Libre</strong>?
              <br />
              Dejarán de sincronizarse sus pedidos.
            </>
          }
          confirmText="Desconectar"
          onClose={() => setConfirmDisconnectDialog({ open: false, accountId: null })}
          onConfirm={confirmDisconnectAccount}
      />

      <ConfirmDialog
          open={Boolean(confirmDisconnectShop)}
          title="Desconectar tienda"
          message={
            <>
              ¿Estás seguro que deseas desconectar la tienda <strong>{confirmDisconnectShop}</strong>?
              <br />
              Dejarán de sincronizarse sus pedidos.
            </>
          }
          confirmText="Desconectar"
          onClose={() => setConfirmDisconnectShop(null)}
          onConfirm={() => handleDisconnectShopifyShop(confirmDisconnectShop)}
      />
    </Grid>
  );
};

export default PluginsIndex;
